import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import ModuleLayout from "../../components/admin/ModuleLayout"
import { CheckRow, inputCls, btnSave, btnSecondary } from "../../components/admin/FormComponents"
import { Modal } from "../../components/ui/modal"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { LoadState } from "@/components/ui/LoadState"

type DeleteWarning = { enrollments: number; approvedPayments: number }

type ProgramCourseLink = {
  courseId: string
  course: { id: string; title: string; slug: string; isPublished: boolean }
}

type ProgramDetail = {
  id: string
  name: string
  slug: string
  description?: string | null
  shortDescription?: string | null
  isPublished: boolean
  courses: ProgramCourseLink[]
}

type Course = {
  id: string
  title: string
  isPublished: boolean
  lesson_count?: number
  student_count?: number
}

export default function ProgramDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [program, setProgram] = useState<ProgramDetail | null>(null)
  const [allCourses, setAllCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([])
  const [savingCourses, setSavingCourses] = useState(false)
  const [courseSearch, setCourseSearch] = useState("")

  const [deleteWarning, setDeleteWarning] = useState<DeleteWarning | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const [programRes, coursesRes] = await Promise.all([
        api.get(`/academy/programs/admin/${id}`),
        api.get("/academy/courses/admin/all?limit=200&page=1"),
      ])
      const p: ProgramDetail = programRes.data?.data
      setProgram(p)
      setSelectedCourseIds((p.courses ?? []).map((cp) => cp.courseId))
      setAllCourses(coursesRes.data?.data?.courses ?? [])
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void load()
  }, [load])

  function toggleCourse(courseId: string) {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((x) => x !== courseId) : [...prev, courseId],
    )
  }

  async function onSaveCourses() {
    if (!id) return
    setSavingCourses(true)
    setError("")
    setSuccess("")
    try {
      await api.post(`/academy/programs/admin/${id}/courses`, { course_ids: selectedCourseIds })
      setSuccess("Cursos actualizados")
      await load()
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setSavingCourses(false)
    }
  }

  async function onDelete(force: boolean) {
    if (!id) return
    setDeleting(true)
    setError("")
    try {
      await api.delete(`/academy/programs/admin/${id}${force ? "?force=true" : ""}`)
      navigate("/programs/programs", { state: { success: "Ruta académica eliminada" } })
    } catch (err) {
      const details = (err as { response?: { status?: number; data?: { details?: DeleteWarning } } }).response
      if (details?.status === 409 && details.data?.details) {
        setDeleteWarning(details.data.details)
      } else {
        setError(getErrorMessage(err))
      }
    } finally {
      setDeleting(false)
    }
  }

  const filteredCourses = useMemo(() => {
    const q = courseSearch.trim().toLowerCase()
    if (!q) return allCourses
    return allCourses.filter((c) => c.title.toLowerCase().includes(q))
  }, [allCourses, courseSearch])

  const assignedCourses = useMemo(() => {
    if (!program) return []
    return program.courses
      .map((cp) => allCourses.find((c) => c.id === cp.courseId))
      .filter((c): c is Course => Boolean(c))
  }, [program, allCourses])

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadState label="Cargando programa..." />
      </div>
    )
  }

  if (!program) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <p className="text-gray-500">{error || "Programa no encontrado."}</p>
        <button type="button" onClick={() => navigate("/programs/programs")} className="text-sm text-brand-500 hover:underline">
          ← Volver a rutas académicas
        </button>
      </div>
    )
  }

  return (
    <ModuleLayout
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "Programas",
        { label: "Rutas académicas", to: "/programs/programs" },
        program.name,
      ]}
      title={program.name}
      subtitle={program.shortDescription || "Ruta académica"}
      tabs={[]}
      metrics={[
        {
          label: "Cursos asignados",
          value: String(program.courses.length),
          tone: program.courses.length === 0 ? "warning" : "neutral",
          hint: program.courses.length === 0 ? "Sin cursos: la ruta no ofrece contenido todavía" : undefined,
        },
        {
          label: "Estado",
          value: program.isPublished ? "Publicado" : "Borrador",
          tone: program.isPublished ? "good" : "warning",
        },
      ]}
      actions={
        <>
          <button
            type="button"
            onClick={() => navigate(`/programs/programs/${id}/edit`)}
            className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-white/[0.03] transition-colors"
          >
            Editar ruta
          </button>
          <button
            type="button"
            disabled={deleting}
            onClick={() => setConfirmDelete(true)}
            className="rounded-xl border border-error-200 px-4 py-2 text-sm font-medium text-error-600 hover:bg-error-50 disabled:opacity-50 dark:border-error-500/30 dark:text-error-400 dark:hover:bg-error-500/10 transition-colors"
          >
            {deleting ? "Eliminando..." : "Eliminar ruta"}
          </button>
        </>
      }
    >
      {error ? (
        <p className="rounded-xl border border-error-200 bg-error-50 px-4 py-2 text-sm text-error-600 dark:border-error-500/20 dark:bg-error-500/10 dark:text-error-400">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="rounded-xl border border-success-200 bg-success-50 px-4 py-2 text-sm text-success-700 dark:border-success-500/20 dark:bg-success-500/10 dark:text-success-400">
          {success}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* Content tree: assigned courses */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Contenido del programa</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Cursos que componen esta ruta, con sus lecciones.
          </p>
          {assignedCourses.length === 0 ? (
            <div className="mt-4 rounded-xl border-2 border-dashed border-gray-200 p-8 text-center dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">Aún no hay cursos asignados a este programa.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              {assignedCourses.map((course) => (
                <button
                  key={course.id}
                  type="button"
                  onClick={() => navigate(`/academy/courses/${course.id}`)}
                  className="flex w-full items-center justify-between rounded-xl border border-gray-200 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/[0.02]"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{course.title}</p>
                    <p className="text-xs text-gray-500">{course.lesson_count ?? 0} lecciones</p>
                  </div>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      course.isPublished
                        ? "bg-[color:rgba(34,197,94,0.14)] text-success-700 dark:text-success-300"
                        : "bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400"
                    }`}
                  >
                    {course.isPublished ? "Publicado" : "Borrador"}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Inline course assignment checklist */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Asignar cursos</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Selecciona los cursos que pertenecen a esta ruta.
          </p>
          <input
            value={courseSearch}
            onChange={(e) => setCourseSearch(e.target.value)}
            placeholder="Buscar curso..."
            className={`mt-3 ${inputCls}`}
          />
          <div className="mt-2 max-h-80 space-y-0.5 overflow-y-auto pr-1">
            {filteredCourses.length === 0 ? (
              <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">Sin resultados</p>
            ) : (
              filteredCourses.map((course) => (
                <CheckRow
                  key={course.id}
                  checked={selectedCourseIds.includes(course.id)}
                  onChange={() => toggleCourse(course.id)}
                  label={course.title}
                  badge={!course.isPublished ? "Borrador" : undefined}
                />
              ))
            )}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              {selectedCourseIds.length} seleccionado{selectedCourseIds.length !== 1 ? "s" : ""}
            </span>
            <button type="button" disabled={savingCourses} onClick={() => void onSaveCourses()} className={btnSave}>
              {savingCourses ? "Guardando..." : "Guardar cursos"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        title="¿Eliminar esta ruta académica?"
        message="Se eliminará la ruta. Si tiene inscripciones o pagos asociados, te lo avisaremos antes de borrar nada."
        confirmLabel="Eliminar ruta"
        confirming={deleting}
        onConfirm={() => {
          setConfirmDelete(false)
          void onDelete(false)
        }}
        onCancel={() => setConfirmDelete(false)}
      />

      <Modal isOpen={deleteWarning !== null} onClose={() => setDeleteWarning(null)} className="max-w-md p-6">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          Esta ruta tiene actividad de estudiantes
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Eliminarla borrará permanentemente:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-600 dark:text-gray-300">
          <li>{deleteWarning?.enrollments ?? 0} inscripción(es) activa(s) a niveles de esta ruta</li>
          <li>{deleteWarning?.approvedPayments ?? 0} pago(s) aprobado(s) asociados a esos niveles</li>
        </ul>
        <p className="mt-3 text-sm font-medium text-error-600 dark:text-error-400">
          Esta acción no se puede deshacer.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={() => setDeleteWarning(null)} className={btnSecondary}>
            Cancelar
          </button>
          <button
            type="button"
            disabled={deleting}
            onClick={() => {
              setDeleteWarning(null)
              void onDelete(true)
            }}
            className="rounded-xl bg-error-600 px-4 py-2 text-sm font-medium text-white hover:bg-error-700 disabled:opacity-50 transition-colors"
          >
            Sí, eliminar de todas formas
          </button>
        </div>
      </Modal>
    </ModuleLayout>
  )
}
